// src/lib/taxonomy/certification-map.ts
// Certification mappings for capability inference
// Focus: Certifications relevant to federal contracting

import { CertificationNode, ClearanceNode, ClearanceLevel } from './types';

export const CERTIFICATION_MAP: CertificationNode[] = [
  // ============================================================
  // PROJECT MANAGEMENT CERTIFICATIONS
  // ============================================================
  {
    id: 'pmp',
    name: 'Project Management Professional',
    aliases: ['PMP', 'PMP Certified', 'PMI PMP', 'PMP Certification'],
    issuer: 'Project Management Institute (PMI)',
    category: 'project_management',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'project_management', confidence: 0.95 },
      { capability: 'schedule_management', confidence: 0.90 },
      { capability: 'risk_management', confidence: 0.90 },
      { capability: 'budget_management', confidence: 0.85 },
      { capability: 'stakeholder_management', confidence: 0.90 },
      { capability: 'resource_management', confidence: 0.85 },
      { capability: 'earned_value_management', confidence: 0.80 }
    ]
  },
  {
    id: 'pmi_acp',
    name: 'PMI Agile Certified Practitioner',
    aliases: ['PMI-ACP', 'ACP', 'Agile Certified'],
    issuer: 'Project Management Institute (PMI)',
    category: 'project_management',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'agile_methodologies', confidence: 0.95 },
      { capability: 'scrum', confidence: 0.90 },
      { capability: 'kanban', confidence: 0.85 },
      { capability: 'iterative_development', confidence: 0.90 },
      { capability: 'agile_project_management', confidence: 0.95 }
    ]
  },
  {
    id: 'csm',
    name: 'Certified ScrumMaster',
    aliases: ['CSM', 'Scrum Master', 'ScrumMaster Certified'],
    issuer: 'Scrum Alliance',
    category: 'project_management',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'scrum', confidence: 0.95 },
      { capability: 'agile_methodologies', confidence: 0.90 },
      { capability: 'team_facilitation', confidence: 0.90 },
      { capability: 'iterative_development', confidence: 0.85 }
    ]
  },
  {
    id: 'safe_agilist',
    name: 'SAFe Agilist',
    aliases: ['SAFe', 'Scaled Agile', 'SAFe Certified', 'SA', 'SAFe 5', 'SAFe 6'],
    issuer: 'Scaled Agile, Inc.',
    category: 'project_management',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'scaled_agile', confidence: 0.95 },
      { capability: 'agile_methodologies', confidence: 0.95 },
      { capability: 'enterprise_agile', confidence: 0.90 },
      { capability: 'large_program_management', confidence: 0.85 },
      { capability: 'lean_portfolio_management', confidence: 0.85 }
    ]
  },

  // ============================================================
  // CLOUD CERTIFICATIONS - AWS
  // ============================================================
  {
    id: 'aws_solutions_architect_associate',
    name: 'AWS Certified Solutions Architect - Associate',
    aliases: ['AWS SA', 'AWS Solutions Architect', 'AWS SAA', 'SAA-C03'],
    issuer: 'Amazon Web Services',
    category: 'cloud',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'cloud_architecture', confidence: 0.95 },
      { capability: 'aws_services', confidence: 0.95 },
      { capability: 'infrastructure_design', confidence: 0.90 },
      { capability: 'scalable_architecture', confidence: 0.85 },
      { capability: 'cost_optimization', confidence: 0.80 }
    ]
  },
  {
    id: 'aws_solutions_architect_professional',
    name: 'AWS Certified Solutions Architect - Professional',
    aliases: ['AWS SA Pro', 'AWS SAP', 'SAP-C02'],
    issuer: 'Amazon Web Services',
    category: 'cloud',
    level: 'Professional',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'cloud_architecture', confidence: 0.98 },
      { capability: 'aws_services', confidence: 0.98 },
      { capability: 'enterprise_architecture', confidence: 0.95 },
      { capability: 'complex_cloud_solutions', confidence: 0.95 },
      { capability: 'migration_strategies', confidence: 0.90 },
      { capability: 'cost_optimization', confidence: 0.90 }
    ]
  },
  {
    id: 'aws_developer_associate',
    name: 'AWS Certified Developer - Associate',
    aliases: ['AWS Developer', 'AWS DVA', 'DVA-C02'],
    issuer: 'Amazon Web Services',
    category: 'cloud',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'aws_services', confidence: 0.95 },
      { capability: 'cloud_development', confidence: 0.95 },
      { capability: 'serverless_development', confidence: 0.85 },
      { capability: 'ci_cd', confidence: 0.80 }
    ]
  },
  {
    id: 'aws_sysops_associate',
    name: 'AWS Certified SysOps Administrator - Associate',
    aliases: ['AWS SysOps', 'AWS SOA', 'SOA-C02'],
    issuer: 'Amazon Web Services',
    category: 'cloud',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'aws_services', confidence: 0.95 },
      { capability: 'cloud_operations', confidence: 0.95 },
      { capability: 'monitoring_logging', confidence: 0.90 },
      { capability: 'automation', confidence: 0.85 }
    ]
  },
  {
    id: 'aws_security_specialty',
    name: 'AWS Certified Security - Specialty',
    aliases: ['AWS Security', 'AWS SCS', 'SCS-C02'],
    issuer: 'Amazon Web Services',
    category: 'cloud',
    level: 'Specialty',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'aws_security', confidence: 0.98 },
      { capability: 'cloud_security', confidence: 0.95 },
      { capability: 'identity_management', confidence: 0.90 },
      { capability: 'security_architecture', confidence: 0.90 },
      { capability: 'compliance', confidence: 0.85 }
    ]
  },

  // ============================================================
  // CLOUD CERTIFICATIONS - AZURE
  // ============================================================
  {
    id: 'azure_fundamentals',
    name: 'Microsoft Certified: Azure Fundamentals',
    aliases: ['AZ-900', 'Azure Fundamentals'],
    issuer: 'Microsoft',
    category: 'cloud',
    level: 'Fundamentals',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'azure_services', confidence: 0.80 },
      { capability: 'cloud_concepts', confidence: 0.85 }
    ]
  },
  {
    id: 'azure_administrator',
    name: 'Microsoft Certified: Azure Administrator Associate',
    aliases: ['AZ-104', 'Azure Administrator', 'Azure Admin'],
    issuer: 'Microsoft',
    category: 'cloud',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'azure_services', confidence: 0.95 },
      { capability: 'cloud_administration', confidence: 0.95 },
      { capability: 'identity_management', confidence: 0.85 },
      { capability: 'virtual_networks', confidence: 0.85 }
    ]
  },
  {
    id: 'azure_solutions_architect',
    name: 'Microsoft Certified: Azure Solutions Architect Expert',
    aliases: ['AZ-305', 'Azure Architect', 'Azure Solutions Architect'],
    issuer: 'Microsoft',
    category: 'cloud',
    level: 'Expert',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'azure_services', confidence: 0.98 },
      { capability: 'cloud_architecture', confidence: 0.98 },
      { capability: 'enterprise_architecture', confidence: 0.90 },
      { capability: 'hybrid_cloud', confidence: 0.90 }
    ]
  },
  {
    id: 'azure_security_engineer',
    name: 'Microsoft Certified: Azure Security Engineer Associate',
    aliases: ['AZ-500', 'Azure Security Engineer'],
    issuer: 'Microsoft',
    category: 'cloud',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'azure_security', confidence: 0.95 },
      { capability: 'cloud_security', confidence: 0.90 },
      { capability: 'identity_management', confidence: 0.90 },
      { capability: 'security_operations', confidence: 0.85 }
    ]
  },

  // ============================================================
  // SECURITY CERTIFICATIONS
  // ============================================================
  {
    id: 'cissp',
    name: 'Certified Information Systems Security Professional',
    aliases: ['CISSP', 'CISSP Certified', 'ISC2 CISSP'],
    issuer: '(ISC)²',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'information_security', confidence: 0.98 },
      { capability: 'security_management', confidence: 0.95 },
      { capability: 'security_architecture', confidence: 0.90 },
      { capability: 'risk_management', confidence: 0.90 },
      { capability: 'security_governance', confidence: 0.90 },
      { capability: 'dod_8570_compliance', confidence: 0.95 }
    ]
  },
  {
    id: 'cism',
    name: 'Certified Information Security Manager',
    aliases: ['CISM', 'ISACA CISM'],
    issuer: 'ISACA',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'security_management', confidence: 0.98 },
      { capability: 'information_security', confidence: 0.95 },
      { capability: 'risk_management', confidence: 0.90 },
      { capability: 'incident_management', confidence: 0.85 },
      { capability: 'security_governance', confidence: 0.90 }
    ]
  },
  {
    id: 'cisa',
    name: 'Certified Information Systems Auditor',
    aliases: ['CISA', 'ISACA CISA'],
    issuer: 'ISACA',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'it_audit', confidence: 0.98 },
      { capability: 'security_assessment', confidence: 0.90 },
      { capability: 'compliance', confidence: 0.90 },
      { capability: 'risk_assessment', confidence: 0.85 },
      { capability: 'governance', confidence: 0.85 }
    ]
  },
  {
    id: 'security_plus',
    name: 'CompTIA Security+',
    aliases: ['Security+', 'Sec+', 'CompTIA Sec+', 'SY0-601', 'SY0-701'],
    issuer: 'CompTIA',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'information_security', confidence: 0.90 },
      { capability: 'network_security', confidence: 0.85 },
      { capability: 'threat_analysis', confidence: 0.80 },
      { capability: 'cryptography', confidence: 0.75 },
      { capability: 'dod_8570_compliance', confidence: 0.90 }
    ]
  },
  {
    id: 'ceh',
    name: 'Certified Ethical Hacker',
    aliases: ['CEH', 'EC-Council CEH', 'Ethical Hacker'],
    issuer: 'EC-Council',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'penetration_testing', confidence: 0.95 },
      { capability: 'vulnerability_assessment', confidence: 0.90 },
      { capability: 'ethical_hacking', confidence: 0.95 },
      { capability: 'security_assessment', confidence: 0.85 },
      { capability: 'dod_8570_compliance', confidence: 0.85 }
    ]
  },
  {
    id: 'oscp',
    name: 'Offensive Security Certified Professional',
    aliases: ['OSCP', 'OffSec OSCP'],
    issuer: 'Offensive Security',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'penetration_testing', confidence: 0.98 },
      { capability: 'vulnerability_assessment', confidence: 0.95 },
      { capability: 'ethical_hacking', confidence: 0.95 },
      { capability: 'security_research', confidence: 0.85 }
    ]
  },
  {
    id: 'casp',
    name: 'CompTIA Advanced Security Practitioner',
    aliases: ['CASP', 'CASP+', 'CompTIA CASP+', 'CAS-004'],
    issuer: 'CompTIA',
    category: 'security',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'security_architecture', confidence: 0.95 },
      { capability: 'enterprise_security', confidence: 0.90 },
      { capability: 'risk_management', confidence: 0.85 },
      { capability: 'security_engineering', confidence: 0.90 },
      { capability: 'dod_8570_compliance', confidence: 0.95 }
    ]
  },

  // ============================================================
  // IT SERVICE MANAGEMENT
  // ============================================================
  {
    id: 'itil_foundation',
    name: 'ITIL Foundation',
    aliases: ['ITIL', 'ITIL v4', 'ITIL 4 Foundation', 'ITIL Certified'],
    issuer: 'Axelos',
    category: 'it_service_management',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'it_service_management', confidence: 0.90 },
      { capability: 'itsm', confidence: 0.90 },
      { capability: 'service_delivery', confidence: 0.85 },
      { capability: 'incident_management', confidence: 0.80 },
      { capability: 'change_management', confidence: 0.80 }
    ]
  },
  {
    id: 'itil_expert',
    name: 'ITIL Expert',
    aliases: ['ITIL Managing Professional', 'ITIL MP', 'ITIL Strategic Leader'],
    issuer: 'Axelos',
    category: 'it_service_management',
    level: 'Expert',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'it_service_management', confidence: 0.98 },
      { capability: 'itsm', confidence: 0.98 },
      { capability: 'service_strategy', confidence: 0.95 },
      { capability: 'continual_improvement', confidence: 0.90 }
    ]
  },

  // ============================================================
  // NETWORKING CERTIFICATIONS
  // ============================================================
  {
    id: 'ccna',
    name: 'Cisco Certified Network Associate',
    aliases: ['CCNA', 'Cisco CCNA', 'CCNA Certified'],
    issuer: 'Cisco',
    category: 'networking',
    level: 'Associate',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'network_engineering', confidence: 0.90 },
      { capability: 'routing_switching', confidence: 0.90 },
      { capability: 'network_fundamentals', confidence: 0.95 },
      { capability: 'cisco_technologies', confidence: 0.90 }
    ]
  },
  {
    id: 'ccnp',
    name: 'Cisco Certified Network Professional',
    aliases: ['CCNP', 'Cisco CCNP', 'CCNP Enterprise', 'CCNP Security'],
    issuer: 'Cisco',
    category: 'networking',
    level: 'Professional',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'network_engineering', confidence: 0.95 },
      { capability: 'advanced_routing_switching', confidence: 0.95 },
      { capability: 'enterprise_networking', confidence: 0.90 },
      { capability: 'cisco_technologies', confidence: 0.95 }
    ]
  },
  {
    id: 'network_plus',
    name: 'CompTIA Network+',
    aliases: ['Network+', 'Net+', 'CompTIA Net+', 'N10-008'],
    issuer: 'CompTIA',
    category: 'networking',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'network_fundamentals', confidence: 0.90 },
      { capability: 'network_troubleshooting', confidence: 0.85 },
      { capability: 'network_infrastructure', confidence: 0.80 }
    ]
  },

  // ============================================================
  // DATA & ANALYTICS CERTIFICATIONS
  // ============================================================
  {
    id: 'aws_data_analytics',
    name: 'AWS Certified Data Analytics - Specialty',
    aliases: ['AWS Data Analytics', 'AWS DAS', 'DAS-C01'],
    issuer: 'Amazon Web Services',
    category: 'data',
    level: 'Specialty',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'data_analytics', confidence: 0.95 },
      { capability: 'big_data', confidence: 0.90 },
      { capability: 'data_engineering', confidence: 0.90 },
      { capability: 'aws_services', confidence: 0.85 }
    ]
  },
  {
    id: 'azure_data_engineer',
    name: 'Microsoft Certified: Azure Data Engineer Associate',
    aliases: ['DP-203', 'Azure Data Engineer'],
    issuer: 'Microsoft',
    category: 'data',
    level: 'Associate',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'data_engineering', confidence: 0.95 },
      { capability: 'azure_services', confidence: 0.90 },
      { capability: 'data_pipelines', confidence: 0.90 },
      { capability: 'big_data', confidence: 0.85 }
    ]
  },

  // ============================================================
  // QUALITY & PROCESS CERTIFICATIONS
  // ============================================================
  {
    id: 'six_sigma_green',
    name: 'Six Sigma Green Belt',
    aliases: ['Green Belt', 'SSGB', 'Lean Six Sigma Green Belt', 'LSSGB'],
    issuer: 'Various (ASQ, IASSC)',
    category: 'quality',
    federalRelevance: 'MEDIUM',
    proves: [
      { capability: 'process_improvement', confidence: 0.90 },
      { capability: 'quality_management', confidence: 0.85 },
      { capability: 'data_analysis', confidence: 0.80 },
      { capability: 'lean_methodologies', confidence: 0.85 }
    ]
  },
  {
    id: 'six_sigma_black',
    name: 'Six Sigma Black Belt',
    aliases: ['Black Belt', 'SSBB', 'Lean Six Sigma Black Belt', 'LSSBB'],
    issuer: 'Various (ASQ, IASSC)',
    category: 'quality',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'process_improvement', confidence: 0.98 },
      { capability: 'quality_management', confidence: 0.95 },
      { capability: 'statistical_analysis', confidence: 0.90 },
      { capability: 'lean_methodologies', confidence: 0.95 },
      { capability: 'change_management', confidence: 0.85 }
    ]
  },

  // ============================================================
  // FEDERAL/COMPLIANCE CERTIFICATIONS
  // ============================================================
  {
    id: 'fedramp',
    name: 'FedRAMP Authorized',
    aliases: ['FedRAMP', 'FedRAMP Certification', 'FedRAMP ATO'],
    issuer: 'FedRAMP PMO (GSA)',
    category: 'industry_specific',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'fedramp_compliance', confidence: 0.98 },
      { capability: 'federal_cloud_security', confidence: 0.95 },
      { capability: 'nist_compliance', confidence: 0.90 },
      { capability: 'continuous_monitoring', confidence: 0.85 }
    ]
  },
  {
    id: 'cmmc_level2',
    name: 'CMMC Level 2 Certified',
    aliases: ['CMMC', 'CMMC 2.0', 'CMMC Level 2', 'Cybersecurity Maturity Model'],
    issuer: 'DoD / CMMC-AB',
    category: 'industry_specific',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'cmmc_compliance', confidence: 0.98 },
      { capability: 'dod_contractor_security', confidence: 0.95 },
      { capability: 'cui_protection', confidence: 0.90 },
      { capability: 'nist_800_171_compliance', confidence: 0.90 }
    ]
  },
  {
    id: 'iso_27001_auditor',
    name: 'ISO 27001 Lead Auditor',
    aliases: ['ISO 27001', 'ISMS Auditor', 'ISO 27001 LA'],
    issuer: 'Various (BSI, PECB, IRCA)',
    category: 'industry_specific',
    federalRelevance: 'HIGH',
    proves: [
      { capability: 'information_security_management', confidence: 0.95 },
      { capability: 'security_auditing', confidence: 0.95 },
      { capability: 'compliance', confidence: 0.90 },
      { capability: 'risk_assessment', confidence: 0.85 }
    ]
  }
];

// ============================================================
// SECURITY CLEARANCES
// ============================================================
export const CLEARANCE_MAP: ClearanceNode[] = [
  {
    id: 'public_trust',
    name: 'Public Trust',
    aliases: ['Public Trust Clearance', 'Moderate Risk Public Trust', 'High Risk Public Trust', 'MBI', 'BI'],
    level: 'PUBLIC_TRUST',
    accessLevel: 'Sensitive but unclassified information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.80 },
      { capability: 'federal_background_check', confidence: 0.95 },
      { capability: 'sensitive_position_work', confidence: 0.85 }
    ]
  },
  {
    id: 'confidential',
    name: 'Confidential Clearance',
    aliases: ['Confidential', 'Confidential Security Clearance'],
    level: 'CONFIDENTIAL',
    accessLevel: 'Confidential classified information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.90 },
      { capability: 'classified_work', confidence: 0.85 },
      { capability: 'federal_security_clearance', confidence: 0.90 }
    ]
  },
  {
    id: 'secret',
    name: 'Secret Clearance',
    aliases: ['Secret', 'Secret Security Clearance', 'S Clearance'],
    level: 'SECRET',
    accessLevel: 'Secret classified information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.95 },
      { capability: 'classified_work', confidence: 0.95 },
      { capability: 'federal_security_clearance', confidence: 0.95 },
      { capability: 'dod_contract_work', confidence: 0.85 },
      { capability: 'sensitive_compartmented_access', confidence: 0.70 }
    ]
  },
  {
    id: 'top_secret',
    name: 'Top Secret Clearance',
    aliases: ['Top Secret', 'TS', 'TS Clearance', 'Top Secret Security Clearance'],
    level: 'TOP_SECRET',
    accessLevel: 'Top Secret classified information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.98 },
      { capability: 'classified_work', confidence: 0.98 },
      { capability: 'federal_security_clearance', confidence: 0.98 },
      { capability: 'high_security_work', confidence: 0.95 },
      { capability: 'dod_contract_work', confidence: 0.95 }
    ]
  },
  {
    id: 'ts_sci',
    name: 'Top Secret/SCI',
    aliases: ['TS/SCI', 'Top Secret SCI', 'TS-SCI', 'SCI Access', 'Sensitive Compartmented Information'],
    level: 'TS_SCI',
    accessLevel: 'Top Secret and Sensitive Compartmented Information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.99 },
      { capability: 'classified_work', confidence: 0.99 },
      { capability: 'federal_security_clearance', confidence: 0.99 },
      { capability: 'intelligence_community_work', confidence: 0.95 },
      { capability: 'high_security_work', confidence: 0.98 },
      { capability: 'sensitive_compartmented_access', confidence: 0.98 }
    ]
  },
  {
    id: 'q_clearance',
    name: 'Q Clearance',
    aliases: ['Q', 'DOE Q Clearance', 'Q Security Clearance'],
    level: 'Q_CLEARANCE',
    accessLevel: 'DOE Top Secret equivalent for nuclear information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.98 },
      { capability: 'classified_work', confidence: 0.98 },
      { capability: 'doe_contract_work', confidence: 0.95 },
      { capability: 'nuclear_related_work', confidence: 0.90 }
    ]
  },
  {
    id: 'l_clearance',
    name: 'L Clearance',
    aliases: ['L', 'DOE L Clearance', 'L Security Clearance'],
    level: 'L_CLEARANCE',
    accessLevel: 'DOE Secret equivalent for nuclear information',
    proves: [
      { capability: 'cleared_personnel', confidence: 0.95 },
      { capability: 'classified_work', confidence: 0.90 },
      { capability: 'doe_contract_work', confidence: 0.90 }
    ]
  }
];

/**
 * Find a certification by ID, name, or alias
 */
export function findCertification(term: string): CertificationNode | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  return CERTIFICATION_MAP.find(cert =>
    cert.id === normalizedTerm ||
    cert.name.toLowerCase() === normalizedTerm ||
    cert.aliases.some(alias => alias.toLowerCase() === normalizedTerm)
  );
}

/**
 * Find a clearance by ID, name, or alias
 */
export function findClearance(term: string): ClearanceNode | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  return CLEARANCE_MAP.find(clearance =>
    clearance.id === normalizedTerm ||
    clearance.name.toLowerCase() === normalizedTerm ||
    clearance.aliases.some(alias => alias.toLowerCase() === normalizedTerm)
  );
}

/**
 * Search certifications (partial match)
 */
export function searchCertifications(term: string): CertificationNode[] {
  const normalizedTerm = term.toLowerCase().trim();

  return CERTIFICATION_MAP.filter(cert =>
    cert.id.includes(normalizedTerm) ||
    cert.name.toLowerCase().includes(normalizedTerm) ||
    cert.aliases.some(alias => alias.toLowerCase().includes(normalizedTerm))
  );
}

/**
 * Get certifications by category
 */
export function getCertificationsByCategory(category: string): CertificationNode[] {
  return CERTIFICATION_MAP.filter(cert => cert.category === category);
}

/**
 * Get certifications with high federal relevance
 */
export function getHighFederalRelevanceCertifications(): CertificationNode[] {
  return CERTIFICATION_MAP.filter(cert => cert.federalRelevance === 'HIGH');
}

/**
 * Get all capability IDs that certifications can prove
 */
export function getAllCertificationCapabilities(): string[] {
  const capabilities = new Set<string>();

  for (const cert of CERTIFICATION_MAP) {
    for (const proof of cert.proves) {
      capabilities.add(proof.capability);
    }
  }

  for (const clearance of CLEARANCE_MAP) {
    for (const proof of clearance.proves) {
      capabilities.add(proof.capability);
    }
  }

  return Array.from(capabilities).sort();
}

/**
 * Get clearance level hierarchy (for comparison)
 */
export function getClearanceLevelRank(level: ClearanceLevel): number {
  const ranks: Record<ClearanceLevel, number> = {
    PUBLIC_TRUST: 1,
    CONFIDENTIAL: 2,
    SECRET: 3,
    TOP_SECRET: 4,
    TS_SCI: 5,
    L_CLEARANCE: 3,  // Equivalent to Secret
    Q_CLEARANCE: 4   // Equivalent to Top Secret
  };
  return ranks[level];
}
